import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { EventParkingLayerId, EVENT_PARKING_LAYER_CONFIG } from '../config/eventParkingConfig';

interface EventVisibilityState {
  /** Map of event layer IDs to their enabled state */
  enabledEvents: Record<EventParkingLayerId, boolean>;
  /** Which event layers are visible in the sidebar (driven by the Event Names table) */
  enabledLayers: Record<EventParkingLayerId, boolean>;
  /** Enable a specific event */
  enableEvent: (layerId: EventParkingLayerId) => void;
  /** Disable a specific event */
  disableEvent: (layerId: EventParkingLayerId) => void;
  /** Toggle an event's enabled state */
  toggleEvent: (layerId: EventParkingLayerId) => void;
  /** Set multiple events at once */
  setEventsEnabled: (events: Partial<Record<EventParkingLayerId, boolean>>) => void;
  /** Set which layers are visible in the sidebar (from the Event Names table) */
  setEnabledLayers: (layers: Partial<Record<EventParkingLayerId, boolean>>) => void;
  /** Check if an event is enabled */
  isEventEnabled: (layerId: EventParkingLayerId) => boolean;
  /** Get list of all enabled event IDs */
  getEnabledEvents: () => EventParkingLayerId[];
  /** Reset to default state from config */
  resetToDefaults: () => void;
}

// Build initial state — all events start as off regardless of config.enabled.
// config.enabled controls sidebar visibility only, not toggle state.
const buildInitialState = (): Record<EventParkingLayerId, boolean> => {
  return EVENT_PARKING_LAYER_CONFIG.reduce((acc, config) => {
    acc[config.layerId] = false;
    return acc;
  }, {} as Record<EventParkingLayerId, boolean>);
};

// Seed enabledLayers from the static config defaults (overwritten after the remote fetch).
const buildInitialEnabledLayers = (): Record<EventParkingLayerId, boolean> => {
  return EVENT_PARKING_LAYER_CONFIG.reduce((acc, config) => {
    acc[config.layerId] = config.enabled;
    return acc;
  }, {} as Record<EventParkingLayerId, boolean>);
};

export const useEventVisibilityStore = create<EventVisibilityState>()(
  persist(
    (set, get) => ({
      enabledEvents: buildInitialState(),
      enabledLayers: buildInitialEnabledLayers(),

      enableEvent: (layerId) =>
        set((state) => ({
          enabledEvents: { ...state.enabledEvents, [layerId]: true },
        })),

      disableEvent: (layerId) =>
        set((state) => ({
          enabledEvents: { ...state.enabledEvents, [layerId]: false },
        })),

      toggleEvent: (layerId) =>
        set((state) => ({
          enabledEvents: { ...state.enabledEvents, [layerId]: !state.enabledEvents[layerId] },
        })),

      setEventsEnabled: (events) =>
        set((state) => ({
          enabledEvents: { ...state.enabledEvents, ...events },
        })),

      setEnabledLayers: (layers) =>
        set((state) => ({
          enabledLayers: { ...state.enabledLayers, ...layers },
        })),

      isEventEnabled: (layerId) => get().enabledEvents[layerId] ?? false,

      getEnabledEvents: () => {
        const { enabledEvents } = get();
        return Object.entries(enabledEvents)
          .filter(([, enabled]) => enabled)
          .map(([layerId]) => layerId as EventParkingLayerId);
      },

      resetToDefaults: () => set({ enabledEvents: buildInitialState() }),
    }),
    {
      name: 'event-visibility-storage',
      version: 3,
      migrate: () => buildInitialState(),
    }
  )
);
